import React, { useState, useEffect, useRef } from 'react';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import PropTypes from 'prop-types';

const AnimatedNumber = ({
  targetValue,
  duration = 2000,
  prefix = '',
  suffix = '',
  moneySuffix = '',
  from = 0,
  ...props
}) => {
  const [currentValue, setCurrentValue] = useState(from);
  const [hasAnimated, setHasAnimated] = useState(false);
  const elementRef = useRef(null);

  function startAnimation() {
    const startTime = Date.now();
    function animate() {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const newValue = Math.floor(from + (targetValue - from) * progress);
      setCurrentValue(newValue);
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setCurrentValue(targetValue);
      }
    }
    requestAnimationFrame(animate);
  }

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimated) {
            startAnimation();
            setHasAnimated(true);
          }
        });
      },
      { threshold: 0.5 },
    );
    if (elementRef.current) {
      observer.observe(elementRef.current);
    }
    return () => {
      if (elementRef.current) {
        observer.unobserve(elementRef.current);
      }
    };
  }, [hasAnimated]);

  return (
    <Stack
      ref={elementRef}
      sx={{ display: 'flex', flexDirection: 'row', alignItems: 'baseline' }}
      {...props}
    >
      <Typography
        variant="title_1"
        sx={{
          fontSize: { xs: '3rem', ipad: '4rem' },
        }}
      >
        {prefix}{currentValue}
      </Typography>
      <Typography
        variant="title_2"
        sx={{
          fontSize: { xs: '2rem', ipad: '3rem' },
        }}
      >
        {suffix}
      </Typography>
      {moneySuffix && (
        <Typography
          display={{ xs: 'none', s: 'block' }}
          variant="xlarge_1"
          sx={{
            marginLeft: { xs: '0.5rem', ipad: '0.8rem' },
            fontSize: { xs: '1.25rem', ipad: '2.25rem' },
          }}
        >
            {moneySuffix}
        </Typography>
      )}
    </Stack>
  );
};

AnimatedNumber.propTypes = {
  targetValue: PropTypes.number.isRequired,
  duration: PropTypes.number,
  prefix: PropTypes.string,
  suffix: PropTypes.string,
  moneySuffix: PropTypes.string,
  from: PropTypes.number,
};

AnimatedNumber.defaultProps = {
  duration: 2000,
  prefix: '',
  suffix: '',
  moneySuffix: '',
  from: 0,
};

export default AnimatedNumber;
